import React, { useEffect, useState } from "react";
import { Modal } from "../../ui";
import FormInput from "../form/FormInput";
import FormSelect from "../form/FormSelect";
import FormButtons from "../form/FormButtons";
import Baby from "../../../../assets/images/baby.png";
import camera from "../../../../assets/images/camera.png";
import { PatientService } from "../../../services/patientService";

export default function AddChildModal({ isOpen, onClose, childData, setChildData, onSave, isReadOnly }) {
  const [errors, setErrors] = useState({});
  const [options, setOptions] = useState({
    genders: [{ value: "", label: "Select Gender" }],
    bloodGroups: [{ value: "", label: "Select Blood Group" }],
    allergies: [{ value: "", label: "Select Allergies" }],
    medicalConditions: [{ value: "", label: "Select Medical Condition" }],
  });

  /* -------------------- Load Dropdowns -------------------- */
  useEffect(() => {
    if (!isOpen || isReadOnly) return;

    const loadOptions = async () => {
      try {
        const res = await PatientService.getDropdowns();
        const data = res?.data?.data || res?.data || {};

        setOptions({
          genders: [{ value: "", label: "Select Gender" }, ...(data.genders || [])],
          bloodGroups: [{ value: "", label: "Select Blood Group" }, ...(data.bloodGroups || [])],
          allergies: [{ value: "", label: "Select Allergies" }, ...(data.allergies || [])],
          medicalConditions: [{ value: "", label: "Select Medical Condition" }, ...(data.medicalConditions || [])],
        });
      } catch (err) {
        console.error("Dropdown load failed", err);
      }
    };

    loadOptions();
  }, [isOpen, isReadOnly]);

  /* -------------------- Image Upload -------------------- */
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await PatientService.uploadProfilePhoto(formData);
      const imageUrl = response?.data?.imageUrl || response?.data?.data?.imageUrl;

      if (imageUrl) {
        setChildData((prev) => ({ ...prev, profilePhoto: imageUrl }));
      }
    } catch (error) {
      console.error("Image upload failed:", error);
      // Fallback to base64 if upload fails
      const reader = new FileReader();
      reader.onloadend = () => {
        setChildData((prev) => ({ ...prev, profilePhoto: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  /* -------------------- Validation -------------------- */
  const validate = () => {
    const err = {};
    if (!childData.name?.trim()) err.name = "Name is required";
    if (!childData.dateOfBirth) err.dateOfBirth = "Date of birth is required";
    if (!childData.gender) err.gender = "Gender is required";
    if (!childData.weightKg) err.weightKg = "Weight is required";
    if (!childData.heightCm) err.heightCm = "Height is required";
    return err;
  };

  const isFormValid = Object.keys(validate()).length === 0;

  /* -------------------- Handlers -------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setChildData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);

    if (!Object.keys(validationErrors).length) {
      onSave();
      onClose();
    }
  };

  /* -------------------- View Mode -------------------- */
  if (isReadOnly) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Child Details" size="default">
        <div className="space-y-6">
          <div className="flex justify-center">
            <div className="w-28 h-28 rounded-full overflow-hidden">
              <img src={childData.profilePhoto || Baby} alt="Child" className="w-full h-full object-cover" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <Info label="Child Name" value={childData.name} />
            <Info label="Date of Birth" value={childData.dateOfBirth && new Date(childData.dateOfBirth).toLocaleDateString()} />
            <Info label="Gender" value={childData.gender} />
            <Info label="Weight" value={childData.weightKg && `${childData.weightKg} kg`} />
            <Info label="Height" value={childData.heightCm && `${childData.heightCm} cm`} />
            <Info label="Blood Group" value={childData.bloodGroup} />
            <Info label="Pediatrician" value={childData.pediatrician} />
            <Info label="Allergies" value={childData.allergies} />
            <Info label="Medical Conditions" value={childData.medicalConditions} />

            {childData.specialNotes && (
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-600">Special Notes</label>
                <p className="text-base mt-1">{childData.specialNotes}</p>
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <button onClick={onClose} className="px-6 py-2 bg-gray-200 rounded-lg">
              Close
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  /* -------------------- Add Mode -------------------- */
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Child" size="default" contentClass="pt-10" titleClass="pt-3 mt-10">
      <div className="relative  -mx-6 mb-8">
        <div className="w-28 h-28 rounded-full bg-[#B0E6AA] flex items-center justify-center absolute left-1/2 -translate-x-1/2 bottom-[91px] cursor-pointer z-10" onClick={() => document.getElementById("child-img").click()}>
          <img src={childData.profilePhoto || Baby} className="w-full h-full rounded-full flex items-center justify-center w-[70px] h-[70px]" alt="Child" />
        </div>

        <div className="w-8 h-8 rounded-full bg-gradient-to-b from-[#4A9CFF] to-[#4057FE] flex items-center justify-center absolute left-1/2 -translate-x-1/2 bottom-[58px] cursor-pointer z-10" onClick={() => document.getElementById("child-img").click()}>
          <img src={camera} alt="Upload" />
        </div>

        <input id="child-img" type="file" accept="image/*" hidden onChange={handleImageUpload} />
      </div>

      <div className="flex flex-col gap-4">
        <FormInput hideLabel={false} label="Child Name" name="name" value={childData.name} onChange={handleChange} error={errors.name} />
        <FormInput hideLabel={false} label="Date of Birth" type="date" name="dateOfBirth" value={childData.dateOfBirth} onChange={handleChange} error={errors.dateOfBirth} />
        <FormSelect hideLabel={false} label="Gender" name="gender" value={childData.gender || ""} onChange={handleChange} options={options.genders} error={errors.gender} />
        <FormInput hideLabel={false} label="Weight (kg)" type="number" name="weightKg" value={childData.weightKg} onChange={handleChange} error={errors.weightKg} />
        <FormInput hideLabel={false} label="Height (cm)" type="number" name="heightCm" value={childData.heightCm} onChange={handleChange} error={errors.heightCm} />
        <FormSelect hideLabel={false} label="Blood Group" name="bloodGroup" value={childData.bloodGroup || ""} onChange={handleChange} options={options.bloodGroups} />
        <FormInput hideLabel={false} label="Pediatrician" name="pediatrician" value={childData.pediatrician} onChange={handleChange} />
        <FormSelect hideLabel={false} label="Allergies" name="allergies" value={childData.allergies || ""} onChange={handleChange} options={options.allergies} />
        <FormSelect hideLabel={false} label="Medical Conditions" name="medicalConditions" value={childData.medicalConditions || ""} onChange={handleChange} options={options.medicalConditions} />
        <FormInput hideLabel={false} label="Special Notes" name="specialNotes" value={childData.specialNotes} onChange={handleChange} />
      </div>

      <FormButtons onCancel={onClose} onSubmit={handleSubmit} submitLabel="Add Child" mode="add" disabled={!isFormValid} />
    </Modal>
  );
}

/* -------------------- Helper -------------------- */
const Info = ({ label, value }) => (
  <div>
    <label className="text-sm font-medium text-gray-600">{label}</label>
    <p className="text-base mt-1">{value || "—"}</p>
  </div>
);
